import { useCallback, useEffect, useRef, useState, type Ref } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { PersonSummary } from "../../api/entitlementAdmin";
import { searchPeople } from "../../api/people";
import { ManageIcon } from "../shared/ManageIcon";
import { formatPersonName } from "../shared/personDisplay";
import { SearchSelect, type SearchSelectOutcome } from "../shared/SearchSelect";
import styles from "./PeopleSearch.module.css";

/** How wide the result list is at least, in pixels, so a name, an email and a badge fit under the compact field. */
const listMinWidth = 520;

/** True on macOS and iOS, where the shortcut is Cmd+K. Used for the hint text only; both Ctrl+K and Cmd+K work everywhere. */
function isApplePlatform(): boolean {
  return typeof navigator !== "undefined" && /Mac|iPhone|iPad/i.test(navigator.platform);
}

/** The neutral page every result opens, whoever the caller is, so one person has one address (AIA-118 R-7, as changed 2026-10-30). */
function personRoute(person: PersonSummary): string {
  return `/people/${encodeURIComponent(person.id)}`;
}

/** The administration page, offered as Manage only for a person the caller may administer. */
function manageRoute(person: PersonSummary): string {
  return `/admin/users/${encodeURIComponent(person.id)}`;
}

/**
 * The people search in the top bar (AIA-118 R-1, R-4, R-6, R-7). It is one `SearchSelect` for every width: at phone width, and
 * whenever the header says it is `compact` (it is short of room, as it is when the month picker drops its label), the field is
 * replaced by a magnifier button, and activating the button (or pressing Ctrl/Cmd+K) lays the field over the bar.
 * The expanded state is component state, because the layout switch itself is CSS.
 */
export function PeopleSearch({ compact = false, rootRef }: { compact?: boolean; rootRef?: Ref<HTMLDivElement> }): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [value, setValue] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [anyManageable, setAnyManageable] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const [focusRequest, setFocusRequest] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const openRef = useRef<HTMLButtonElement>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const hint = t(isApplePlatform() ? "peopleSearch.hintMac" : "peopleSearch.hintWindows");

  // Ctrl+K or Cmd+K, anywhere on the page, goes to the field and keeps the browser's own shortcut from running.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setExpanded(true);
        setFocusRequest((count) => count + 1);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  // The field may be hidden until `expanded` is drawn, so focus waits for the render that follows the request.
  useEffect(() => {
    if (focusRequest > 0) inputRef.current?.focus();
  }, [focusRequest]);

  useEffect(() => () => controllerRef.current?.abort(), []);

  const search = useCallback(
    async (term: string): Promise<SearchSelectOutcome<PersonSummary>> => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      const outcome = await searchPeople(term, controller.signal);
      if (controller.signal.aborted) {
        return { kind: "ok", items: [] };
      }
      if (outcome.kind === "error") {
        setHasMore(false);
        setAnyManageable(false);
        setAnnouncement("");
        return { kind: "error", message: t("peopleSearch.error") };
      }
      const { people, hasMore: more } = outcome.response;
      setHasMore(more);
      setAnyManageable(people.some((person) => person.canAdminister));
      setAnnouncement(people.length === 0 ? t("peopleSearch.resultsNone") : t("peopleSearch.results", { count: people.length }));
      return { kind: "ok", items: people };
    },
    [t],
  );

  const collapse = () => {
    setExpanded(false);
    openRef.current?.focus();
  };

  const onValueChange = (next: string) => {
    setValue(next);
    if (next.trim().length < 2) setAnnouncement("");
  };

  const open = (route: string) => {
    void navigate(route);
    setValue("");
    setAnnouncement("");
    setExpanded(false);
  };

  const onSelect = (person: PersonSummary) => open(personRoute(person));

  // Shift+Enter: Manage when the caller may administer the person, otherwise the person, as Enter would.
  const onAlternateChoose = (person: PersonSummary) => open(person.canAdminister ? manageRoute(person) : personRoute(person));

  return (
    <div ref={rootRef} className={styles.root} data-expanded={expanded} data-compact={compact} data-apple={isApplePlatform()}>
      <button
        ref={openRef}
        type="button"
        className={styles.openButton}
        aria-label={t("peopleSearch.open")}
        aria-expanded={expanded}
        tabIndex={expanded ? -1 : undefined}
        onClick={() => {
          setExpanded(true);
          setFocusRequest((count) => count + 1);
        }}
      >
        <svg className={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
      </button>
      <SearchSelect<PersonSummary>
        className={styles.field}
        label={t("peopleSearch.label")}
        placeholder={t("peopleSearch.placeholder")}
        value={value}
        onValueChange={onValueChange}
        search={search}
        itemLabel={(person) => formatPersonName(person)}
        itemKey={(person) => person.id}
        renderItem={(person) => <PersonRow person={person} onManage={() => open(manageRoute(person))} />}
        onSelect={onSelect}
        onAlternateChoose={onAlternateChoose}
        autoHighlightFirst
        onEscape={() => {
          if (value.length > 0) onValueChange("");
          else collapse();
        }}
        minLength={2}
        footer={
          hasMore || anyManageable ? (
            <>
              {hasMore && <div>{t("peopleSearch.more")}</div>}
              {anyManageable && (
                <div>
                  <Trans i18nKey="peopleSearch.shortcutsHint" components={{ k: <kbd className={styles.key} /> }} />
                </div>
              )}
            </>
          ) : undefined
        }
        popupMinWidth={listMinWidth}
        popupAlign="end"
        popupClassName={styles.list}
        inputRef={inputRef}
        inputAttributes={{ "aria-keyshortcuts": "Control+K Meta+K", maxLength: 100 }}
        actions={
          <>
            {value.length === 0 && (
              <span className={styles.hint}>
                {hint.split(" ").map((key, index) => (
                  <kbd key={`${key}-${String(index)}`} className={styles.key}>
                    {key}
                  </kbd>
                ))}
              </span>
            )}
            {value.length > 0 && (
              <button
                type="button"
                className={styles.clearButton}
                aria-label={t("peopleSearch.clear")}
                // Keeps focus in the field, so clearing it never closes anything but the results.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  onValueChange("");
                  inputRef.current?.focus();
                }}
              >
                <svg className={styles.clearIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" />
                  <path d="m15 9-6 6m0-6 6 6" />
                </svg>
              </button>
            )}
            {expanded && (
              <button type="button" className={styles.closeButton} aria-label={t("peopleSearch.close")} onClick={collapse}>
                <svg className={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            )}
          </>
        }
      />
      <div className="visually-hidden" role="status" aria-live="polite">
        {announcement}
      </div>
    </div>
  );
}

/**
 * One result: the name (the email when there is none), the email, the status and where the person sits in the organisation.
 *
 * Manage, for a person the caller may administer, is a pointer-only shortcut inside the option: a real button or link here would be a
 * focusable control nested in `role="option"` (axe `nested-interactive`). So it is hidden from assistive technology and takes no
 * focus, and the keyboard route is Shift+Enter on the active option. Screen readers are told that route by the visually hidden text,
 * which is part of the option's name, and by the hint under the list.
 */
function PersonRow({ person, onManage }: { person: PersonSummary; onManage: () => void }): React.JSX.Element {
  const { t } = useTranslation();
  const name = formatPersonName(person);
  const unit = [person.division, person.department].filter((part): part is string => !!part).join(" · ") || t("budgets.noDivision");
  return (
    <span className={styles.row}>
      <span className={styles.rowTop}>
        <span className={styles.name}>{name}</span>
        <span className={styles.rowEnd}>
          {/* Nearly everyone is Active, so only the exceptions (Pending, Inactive) are marked. */}
          {person.status !== "Active" && (
            <span className={styles.badge} data-status={person.status}>
              {t(`peopleSearch.status.${person.status}`, { defaultValue: person.status })}
            </span>
          )}
          {person.canAdminister && (
            <>
              <span
                className={styles.manage}
                aria-hidden="true"
                onClick={(event) => {
                  event.stopPropagation();
                  onManage();
                }}
              >
                <ManageIcon className={styles.manageIcon} />
                {t("peopleSearch.manage")}
              </span>
              <span className="visually-hidden">{t("peopleSearch.manageAvailable")}</span>
            </>
          )}
        </span>
      </span>
      {name !== person.email && <span className={styles.secondary}>{person.email}</span>}
      <span className={styles.secondary}>{unit}</span>
    </span>
  );
}
